<template>
  <div
    v-if="isPendingAlbum"
    class="aspect-video flex items-center justify-center"
  >
    <LoadingIcon class="text-primary h-10 w-10" />
  </div>
  <div v-else class="page my-16 flex flex-col gap-16 container">
    <UIBreadcrumbs :breadcrumbs="breadcrumbs" />
    <section class="flex flex-col md:flex-row items-centet gap-3 w-full">
      <div class="max-w-[1052px] w-full flex flex-col gap-3">
        <h1 class="font-bold md:text-7xl content-center">
          {{ album?.title }}
        </h1>
        <div
          v-if="album?.description"
          class="max-w-[840px] md:text-[18px]"
          v-html="album?.description"
        />

        <p class="max-w-lg text-gray-500 mt-4">
          Данные материалы не подлежат редактированию (в том числе, логотипы),
          не допускается сокращение их продолжительности.
        </p>
      </div>
    </section>
    <section class="flex flex-col gap-8">
      <div class="filters flex items-end gap-5 flex-wrap md:flex-nowrap">
        <FormKit v-model="filters" type="form" :actions="false">
          <div class="flex gap-5">
            <FormKitSchema :schema="filtersScheme" />
          </div>
        </FormKit>
        <FormKit
          v-model="filters.search"
          type="search"
          placeholder="Поиск"
          :classes="{ outer: 'w-full  max-w-xl' }"
        >
          <template #prefix="context">
            <IconSearch class="flex-none size-4 ml-3" />
          </template>
          <template #suffix="context">
            <button
              v-if="filters.search"
              class="flex-none pl-1 py-2 pr-3"
              @click="filters.search = ''"
            >
              <IconX class="size-4" />
            </button>
          </template>
        </FormKit>
      </div>
      <div class="catalog">
        <DataProvider
          v-model="catalog"
          v-slot="{ isFetchingNextPage, data, isFetchedAfterMount, isFetching }"
          :filters="filters"
          :q-key="`${root}-index`"
          :per-page="12"
          :endpoint="`/${root}`"
        >
          <div
            class="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-x-3 gap-y-6"
          >
            <template v-for="(page, i) in data.pages" :key="i">
              <VideoCard
                v-for="item in page.resources"
                :key="item.id"
                :context="{
                  ...item,
                  title: item.name,
                  subtitle: item.description,
                }"
                type="catalog"
                :is-loading="
                  isFetching && !isFetchingNextPage && isFetchedAfterMount
                "
                @click="
                  openModalVideo({
                    albumId: route.params.albumId,
                    videoId: item.id,
                    returnUrl: `/videos/catalog/${route.params.albumId}`,
                  })
                "
              />
            </template>
          </div>
        </DataProvider>
      </div>
    </section>
  </div>
  <UIFooter />
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import { useQuery } from 'vue-query'
import { useRoute, useRouter } from 'vue-router'

import { IconSearch, IconX } from '@tabler/icons-vue'

import VideoCard from '@/components/common/VideoCard.vue'
import DataProvider from '@/components/DataProvider.js'
import LoadingIcon from '@/components/loading/LoadingIcon.vue'
import VideoModal from '@/components/modals/VideoModal.vue'
import Button from '@/components/ui/UIButton.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import useInfinitePage from '@/plugins/useInfinitePage.js'
import useModalDynamic from '@/plugins/useModalDynamic.js'

import UIBreadcrumbs from '../../../components/ui/UIBreadcrumbs.vue'

type Context = {
  albumId: number | string | string[]
  videoId: number | string | string[]
  returnUrl: string
}

const http = inject('http')

const route = useRoute()
const router = useRouter()
const breadcrumbs = ref([
  {
    name: 'Смотри Заполярье',
    path: '/videos',
  },
  {
    name: 'Каталог',
    path: '/videos/catalog',
  },
])

const { data: album, isLoading: isPendingAlbum } = useQuery({
  queryKey: ['album', route.params.albumId],
  queryFn: () =>
    http(`/albums/${route.params.albumId}`, {
      method: 'GET',
    }),
  select: (data) => {
    breadcrumbs.value.push({ name: data.resource.title, path: '' })
    return data.resource
  },
})

const { root, filters } = useInfinitePage(
  `albums/${route.params.albumId}/videos`,
)

const catalog = ref(null)
const tags = ref([])

const filtersScheme = computed(() => [
  {
    $formkit: 'qselect',
    name: 'tags',
    placeholder: 'Праздники',
    label: 'Категории',
    trackBy: 'id',
    labelBy: 'name',
    options: tags.value,
    classes: {
      outer: 'md:w-[286px]',
    },
  },
])

const openModalVideo = async (context: Context, noPush = false) => {
  if (!noPush) {
    await router.push(`/videos/catalog/${context.albumId}/${context.videoId}`)
  }

  useModalDynamic({
    opened: true,
    component: VideoModal,
    compAttrs: {
      videoId: context.videoId,
      albumId: context.albumId,
      returnUrl: context.returnUrl,
      options: {},
    },
  })
}

const getTags = async () => {
  const tags = await http('/api/common/types/tags/albums_videos')
  if (tags) {
    return tags.resources
  }
}

onMounted(async () => {
  if (route.params.albumId && route.params.videoId) {
    await openModalVideo(
      {
        albumId: route.params.albumId,
        videoId: route.params.videoId,
        returnUrl: `/videos/catalog/${route.params.albumId}`,
      },
      true,
    )
  }
  tags.value = await getTags()
})
</script>
